// 会话与流式回答。
// 发消息：快速问答走 /knowledge-chat/:session，智能体走 /agent-chat/:session；回答是 SSE（event:message + data:{json}）。

import { createSseParser, type SseEvent } from '../lib/sse'
import { LIMITS } from '../lib/limits'
import { request } from './client'
import type { Agent, ChatSession, Message, Model, Page, Reference } from './types'

export const listSessions = (page = 1, pageSize = 30) =>
  request<Page<ChatSession>>('/sessions', { query: { page, page_size: pageSize, source: 'web' } })

export const createSession = (title?: string) => request<{ data: ChatSession }>('/sessions', { body: title ? { title } : {} }).then((r) => r.data)
export const renameSession = (id: string, title: string) => request(`/sessions/${encodeURIComponent(id)}`, { method: 'PUT', body: { title } })
export const deleteSession = (id: string) => request(`/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' })

export const loadMessages = (sessionId: string, opts: { limit?: number; beforeTime?: string } = {}) =>
  request<{ data: Message[] }>(`/messages/${encodeURIComponent(sessionId)}/load`, { query: { limit: opts.limit ?? 30, before_time: opts.beforeTime } }).then((r) => r.data ?? [])

export const stopMessage = (sessionId: string, messageId: string) =>
  request(`/sessions/${encodeURIComponent(sessionId)}/stop`, { body: { message_id: messageId } })

export const listAgents = () => request<{ data: Agent[] }>('/agents').then((r) => r.data ?? [])
/** 对话里能选的模型：问答模型里去掉标了「内部用」的（抽取、摘要用的那个关了思考，不适合对话）。 */
export const listChatModels = () => request<{ data: Model[] }>('/models').then((r) => (r.data ?? []).filter((m) => m.type === 'KnowledgeQA' && !m.description?.startsWith('内部用')))

export const QUICK_ANSWER = 'builtin-quick-answer'
export const isAgentMode = (agent: Agent | undefined) => !!agent && agent.id !== QUICK_ANSWER && agent.config?.agent_mode !== 'quick-answer'

export interface SendInput {
  query: string
  knowledgeBaseIds: string[]
  knowledgeIds?: string[]
  agent?: Agent
  modelId?: string
  /** 提问前缩小了范围（只在「我写的」里找……）：显示在回答的过程里，不发给服务端。 */
  route?: string
  /** 问题先显示出来，再算要不要缩小范围（要取分区）；返回 null 就照原来的范围。 */
  prepare?: () => Promise<Pick<SendInput, 'knowledgeBaseIds' | 'knowledgeIds' | 'route'> | null>
}

/** 回答过程中的事件，界面只认这些。 */
export type ChatEvent =
  | { kind: 'start'; assistantMessageId?: string; userMessageId?: string }
  | { kind: 'thinking'; text: string }
  | { kind: 'thinking-done' }
  | { kind: 'answer'; text: string }
  | { kind: 'references'; references: Reference[] }
  | { kind: 'title'; title: string }
  | { kind: 'tool'; name: string; phase: 'call' | 'result'; ok?: boolean }
  | { kind: 'done' }
  | { kind: 'error'; message: string }

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * 快速问答模式下，思考过程夹在回答里：<think>…</think>。流式时标签可能被切开，
 * 这里一边收一边分：标签里的算思考，外面的算回答；没收完整的半个标签先扣下。
 */
export function createThinkSplitter() {
  let inThink = false
  let pending = ''
  return (chunk: string, final = false): ChatEvent[] => {
    const out: ChatEvent[] = []
    let text = pending + chunk
    pending = ''
    while (text) {
      const tag = inThink ? '</think>' : '<think>'
      const at = text.indexOf(tag)
      if (at >= 0) {
        const before = text.slice(0, at)
        if (before) out.push(inThink ? { kind: 'thinking', text: before } : { kind: 'answer', text: before })
        if (inThink) out.push({ kind: 'thinking-done' })
        inThink = !inThink
        text = text.slice(at + tag.length)
        continue
      }
      // 尾巴可能是半个标签
      let keep = 0
      if (!final) for (let n = Math.min(tag.length - 1, text.length); n > 0; n--) if (tag.startsWith(text.slice(-n))) { keep = n; break }
      const body = text.slice(0, text.length - keep)
      if (body) out.push(inThink ? { kind: 'thinking', text: body } : { kind: 'answer', text: body })
      pending = text.slice(text.length - keep)
      text = ''
    }
    return out
  }
}

/** 一条服务端事件 → 界面事件。 */
export function chatEventsOf(event: SseEvent, splitAnswer: (chunk: string) => ChatEvent[]): ChatEvent[] {
  const text = typeof event.content === 'string' ? event.content : ''
  switch (event.response_type) {
    case 'agent_query': {
      const data = isRecord(event.data) ? event.data : {}
      return [{
        kind: 'start',
        assistantMessageId: typeof event.assistant_message_id === 'string' ? event.assistant_message_id : undefined,
        userMessageId: typeof data.user_message_id === 'string' ? data.user_message_id : undefined,
      }]
    }
    case 'thinking': {
      const out: ChatEvent[] = text ? [{ kind: 'thinking', text }] : []
      if (event.done === true) out.push({ kind: 'thinking-done' })
      return out
    }
    case 'answer':
      return text ? splitAnswer(text) : []
    case 'references':
      return Array.isArray(event.knowledge_references)
        ? [{ kind: 'references', references: (event.knowledge_references as unknown[]).filter(isRecord).slice(0, LIMITS.references) as Reference[] }]
        : []
    case 'session_title':
      return text ? [{ kind: 'title', title: text }] : []
    case 'tool_call':
    case 'tool_result': {
      const data = isRecord(event.data) ? event.data : {}
      const name = typeof data.tool_name === 'string' ? data.tool_name : '工具'
      return [{ kind: 'tool', name, phase: event.response_type === 'tool_call' ? 'call' : 'result', ok: typeof data.success === 'boolean' ? data.success : undefined }]
    }
    case 'complete':
    case 'stop':
      return [{ kind: 'done' }]
    case 'error':
      return [{ kind: 'error', message: text || '回答出错了' }]
    default:
      return []
  }
}

/** 发一条消息，边收边吐出事件。signal 中止时连接断开（服务端的生成另用 stopMessage 停）。 */
export async function* sendMessage(sessionId: string, input: SendInput, signal?: AbortSignal): AsyncGenerator<ChatEvent> {
  const agent = isAgentMode(input.agent)
  const body = {
    query: input.query,
    knowledge_base_ids: input.knowledgeBaseIds,
    knowledge_ids: input.knowledgeIds ?? [],
    agent_enabled: agent,
    // 不指定智能体时不带 agent_id：灵智原生用知识库 / 系统的默认模型（没配模型的智能体在 useAgents 里就筛掉了）
    agent_id: input.agent?.id,
    summary_model_id: input.modelId || undefined,
    web_search_enabled: false,
    channel: 'web',
  }
  const res = await request<Response>(`/${agent ? 'agent-chat' : 'knowledge-chat'}/${encodeURIComponent(sessionId)}`, {
    body,
    raw: true,
    signal,
    headers: { Accept: 'text/event-stream' },
  })
  if (!res.body) throw new Error('服务器没有返回内容')
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  const parser = createSseParser()
  const split = createThinkSplitter()
  let answered = 0
  let finished = false
  try {
    while (true) {
      const { value, done } = await reader.read()
      const events = done ? parser.flush() : parser.push(decoder.decode(value, { stream: true }))
      for (const ev of events) {
        for (const e of chatEventsOf(ev, split)) {
          if (e.kind === 'answer') {
            answered += e.text.length
            if (answered > LIMITS.answerChars) {
              yield { kind: 'error', message: '回答太长，已自动停下' }
              return
            }
          }
          if (e.kind === 'done') finished = true
          yield e
          // 出错以后灵智原生不一定关掉连接：自己收尾，不然界面会一直等着
          if (e.kind === 'error') return
        }
      }
      if (done) break
    }
    for (const e of split('', true)) yield e
    if (!finished) yield { kind: 'done' }
  } finally {
    reader.cancel().catch(() => undefined)
    reader.releaseLock()
  }
}
